// src/components/ExportActions.jsx
import React, { useMemo } from "react";
import { Stack, Button, useMediaQuery, useTheme } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // ✅ function import

const PKR = (n) => `Rs ${n.toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

export default function ExportActions({ month, year, rows, totals }) {
  const small = useMediaQuery("(max-width:900px)");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const fileSuffix = useMemo(
    () =>
      `${new Date(2000, month, 1).toLocaleString("en-US", { month: "short" })}-${year}`,
    [month, year]
  );

  const handleExportExcel = () => {
    const excelRows = rows.map((r) => ({
      "Employee ID": r.id,
      Name: r.name,
      Designation: r.role,
      "Basic Salary": r.basic,
      Allowances: r.allowances,
      Deductions: r.deductions,
      "Advance Paid": r.advance,
      "Net Salary": r.net,
      Status: r.status,
    }));

    excelRows.push({
      "Employee ID": "",
      Name: "",
      Designation: `Total — ${totals.staff} employees`,
      "Basic Salary": totals.sumBasic,
      Allowances: totals.sumAllow,
      Deductions: totals.sumDed,
      "Advance Paid": totals.sumAdv,
      "Net Salary": totals.sumNet,
      Status: `${totals.paid} Paid / ${totals.unpaid} Unpaid`,
    });

    const ws = XLSX.utils.json_to_sheet(excelRows);

    // Format PKR columns (D..H)
    const moneyCols = ["D", "E", "F", "G", "H"];
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = 1; R <= range.e.r; R++) {
      moneyCols.forEach((C) => {
        const addr = `${C}${R + 1}`;
        const cell = ws[addr];
        if (cell && typeof cell.v === "number") {
          cell.t = "s";
          cell.v = PKR(cell.v);
        }
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Salary");
    XLSX.writeFile(wb, `Salary-${fileSuffix}.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt" });
    const title = `Salary Sheet — ${new Date(2000, month, 1).toLocaleString("en-US", {
      month: "long",
    })} ${year}`;

    doc.setFontSize(16);
    doc.text(title, 40, 36);

    const head = [[
      "Employee ID","Name","Designation",
      "Basic Salary","Allowances","Deductions","Advance Paid","Net Salary","Status",
    ]];

    const body = rows.map((r) => [
      r.id,
      r.name,
      r.role,
      PKR(r.basic),
      PKR(r.allowances),
      PKR(r.deductions),
      PKR(r.advance),
      PKR(r.net),
      r.status,
    ]);

    body.push([
      "",
      "",
      `Total — ${totals.staff} employees`,
      PKR(totals.sumBasic),
      PKR(totals.sumAllow),
      PKR(totals.sumDed),
      PKR(totals.sumAdv),
      PKR(totals.sumNet),
      `${totals.paid} Paid / ${totals.unpaid} Unpaid`,
    ]);

    // ✅ use function call, not doc.autoTable
    autoTable(doc, {
      head,
      body,
      startY: 56,
      styles: { fontSize: 10, cellPadding: 6 },
      headStyles: { fillColor: [5, 150, 105], textColor: 255 },
      columnStyles: {
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
      didDrawPage: () => {
        doc.setFontSize(9);
        doc.setTextColor("#555");
        doc.text(
          "* Net = Basic + Allowances − Deductions − Advance Paid. All amounts are in PKR.",
          40,
          doc.internal.pageSize.getHeight() - 20
        );
      },
    });

    doc.save(`Salary-${fileSuffix}.pdf`);
  };

  return (
    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ width: small ? "100%" : "auto" }}>
      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        fullWidth={small}
        onClick={handleExportExcel}
        sx={{
          textTransform: "none",
          borderRadius: 4, px: 3, py: 1.2, fontWeight: 500,
          border: `3px solid ${isDark ? "rgba(86,198,98,0.35)" : "#ddf3ea"}`,
          bgcolor: isDark ? "rgba(86,198,98,0.12)" : "#edfef6",
          color: isDark ? "#7ee2a8" : "#56C662",
          "&:hover": { bgcolor: "rgba(5,150,105,0.2)" },
        }}
      >
        Export Excel
      </Button>

      <Button
        variant="contained"
        color="success"
        startIcon={<PictureAsPdfIcon />}
        fullWidth={small}
        onClick={handleExportPDF}
        sx={{ bgcolor: "#56C662", px: 3, py: 1.2, borderRadius: 4, textTransform: "none" }}
      >
        Export PDF
      </Button>
    </Stack>
  );
}
